<script setup>
defineProps({
  error: String,
  executionDetails: Object // { chain_name, total_steps, completed_steps, failed_steps }
});

defineEmits(['retry', 'back']);

function formatErrorMessage(error) {
  if (!error) return 'An unknown error occurred. Please try again.';

  if (error.includes('timeout') || error.includes('timed out')) {
    return 'The generation process took longer than expected and was stopped. This can happen with complex chains or high server load. Please try again.';
  }
  if (error.includes('validation') || error.includes('Validation')) {
    return 'The AI generated content that didn\'t match the expected format. This is usually temporary. Please try regenerating.';
  }
  if (error.includes('rate limit') || error.includes('too many requests')) {
    return 'We\'ve received too many requests. Please wait a moment and try again.';
  }
  if (error.includes('network') || error.includes('connection')) {
    return 'There was a network connectivity issue. Please check your connection and try again.';
  }
  if (error.includes('authentication') || error.includes('unauthorized')) {
    return 'Your session may have expired. Please refresh the page and try again.';
  }

  return error.charAt(0).toUpperCase() + error.slice(1) + (error.endsWith('.') ? '' : '.');
}
</script>

<template>
  <div class="cr-error">
    <div class="error-content">
      <v-icon size="64" color="error" class="mb-4">mdi-alert-circle-outline</v-icon>

      <h2 class="error-title">Content Generation Failed</h2>
      <p class="error-message">{{ formatErrorMessage(error) }}</p>

      <div v-if="executionDetails" class="error-details">
        <div class="detail-row">
          <span class="detail-label">Chain:</span>
          <span class="detail-value">{{ executionDetails.chain_name }}</span>
        </div>
        <div class="detail-row">
          <span class="detail-label">Progress:</span>
          <span class="detail-value">
            {{ executionDetails.completed_steps }}/{{ executionDetails.total_steps }} steps completed
            <span v-if="executionDetails.failed_steps > 0" class="failed-steps">
              ({{ executionDetails.failed_steps }} failed)
            </span>
          </span>
        </div>
      </div>

      <div class="error-actions">
        <p class="error-message">Would you like to try again?</p>
        <div class="action-buttons">
          <v-btn color="primary" @click="$emit('retry')">
            <v-icon left>mdi-refresh</v-icon>
            Try Again
          </v-btn>
          <v-btn v-if="$listeners.back" outlined class="ml-2" @click="$emit('back')">
            <v-icon left>mdi-arrow-left</v-icon>
            Back
          </v-btn>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.cr-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
}
.error-content {
  text-align: center;
  max-width: 600px;
}
.error-title {
  margin-bottom: 10px;
}
.error-details {
  margin: 20px 0;
  text-align: left;
}
.detail-row {
  padding: 4px 0;
}
.detail-label {
  font-weight: bold;
  margin-right: 8px;
}
.failed-steps {
  color: #f44336;
}
.error-actions {
  margin-top: 20px;
}
.action-buttons {
  margin-top: 10px;
}
</style>
